import React from "react";
import { motion } from "framer-motion";
import {
  BadgeCheck,
  CalendarDays,
  Globe2,
  ShieldCheck,
  UsersRound,
} from "lucide-react";
import { useLang } from "../context/LanguageContext";
import PageHero from "../components/PageHero";
import { CANADA_MAPLE_IMAGE } from "../lib/brand";

const FOUNDER_IMG = "/josias.jpg";

const page = {
  fr: {
    eyebrow: "L’histoire de JNIC",
    title: "Comprendre la procédure change tout.",
    subtitle: "JNIC est né d’une conviction simple : une personne bien informée peut prendre de meilleures décisions et garder le contrôle de son projet canadien.",
    intro1: "Je m’appelle Josias Ndam. Mon propre parcours du Cameroun vers le Canada m’a montré combien l’information peut sembler dispersée, technique ou difficile à vérifier lorsqu’on prépare une démarche depuis l’étranger.",
    intro2: "J’ai commencé par aider des proches à comprendre les étapes, organiser leurs documents et suivre leur progression. Cette expérience s’est ensuite élargie à des personnes, des couples et des familles venant de plusieurs pays francophones.",
    intro3: "Aujourd’hui, JNIC propose de l’information générale, de la préparation pédagogique et du soutien organisationnel. L’objectif n’est pas de retirer au candidat la responsabilité de son dossier, mais de lui donner une méthode, des repères et les bonnes sources à consulter.",
    quote: "Un projet solide commence par une personne qui comprend ce qu’elle fait, pourquoi elle le fait et comment vérifier l’information.",
    role: "Fondateur — information et orientation",
    experienceKicker: "Expérience actuelle",
    experienceTitle: "Des personnes, des couples et des familles",
    experienceText: "Les chiffres ci-dessous présentent l’expérience accumulée à ce jour. Ils ne constituent pas une promesse de résultat pour les futurs dossiers.",
    snapshot: [
      "14 candidats accompagnés avec succès",
      "100% de visas de Résident permanent obtenus",
      "Scores CRS des candidats situés entre 382 et 456 points",
      "Candidats accompagnés au Cameroun 🇨🇲 et en Côte d’Ivoire 🇨🇮",
    ],
    valuesTitle: "Quatre principes. Non négociables.",
    values: [
      { k: "Autonomie", v: "Expliquer la démarche afin que chaque personne comprenne et contrôle son projet." },
      { k: "Bilingue par défaut", v: "Une expérience en français et en anglais, avec le français comme langue principale." },
      { k: "Sources officielles", v: "Revenir aux critères publiés par IRCC et les organismes responsables." },
      { k: "Honnêteté", v: "Présenter les limites, les risques et les étapes sans garantir un résultat." },
    ],
    noticeTitle: "Une distinction importante",
    noticeText: "JNIC n’est pas un représentant autorisé en immigration, un cabinet d’avocats ou une étude notariale. JNIC ne représente pas les candidats auprès d’IRCC et ne fournit pas d’avis juridique. Les services sont informatifs, pédagogiques et organisationnels.",
  },
  en: {
    eyebrow: "The JNIC story",
    title: "Understanding the process changes everything.",
    subtitle: "JNIC grew from one simple belief: a well-informed person can make better decisions and stay in control of their Canadian project.",
    intro1: "My name is Josias Ndam. My own journey from Cameroon to Canada showed me how scattered, technical and difficult to verify information can feel when preparing a process from abroad.",
    intro2: "I began by helping people close to me understand the steps, organize documents and track their progress. That experience later expanded to individuals, couples and families from several French-speaking countries.",
    intro3: "Today, JNIC provides general information, educational preparation and organizational support. The goal is not to remove responsibility from the applicant, but to provide a method, clear reference points and the right sources to consult.",
    quote: "A strong project starts with a person who understands what they are doing, why they are doing it and how to verify the information.",
    role: "Founder — information and orientation",
    experienceKicker: "Current experience",
    experienceTitle: "Individuals, couples and families",
    experienceText: "The figures below describe experience accumulated to date. They are not a promise of results for future files.",
    snapshot: [
      "14 candidates successfully accompanied",
      "100% Permanent Resident visas obtained",
      "Candidate CRS scores ranging between 382 and 456 points",
      "Candidates accompanied in Cameroon 🇨🇲 and Ivory Coast 🇨🇮",
    ],
    valuesTitle: "Four principles. Non-negotiable.",
    values: [
      { k: "Autonomy", v: "Explain the process so each person understands and controls their project." },
      { k: "Bilingual by default", v: "A French and English experience, with French as the primary language." },
      { k: "Official sources", v: "Return to criteria published by IRCC and the responsible organizations." },
      { k: "Honesty", v: "Present limits, risks and steps without guaranteeing an outcome." },
    ],
    noticeTitle: "An important distinction",
    noticeText: "JNIC is not an authorized immigration representative, law firm or notarial office. JNIC does not represent applicants before IRCC and does not provide legal advice. Services are informational, educational and organizational.",
  },
};

const stats = {
  fr: [
    { value: "14", label: "candidats accompagnés", icon: UsersRound },
    { value: "100%", label: "visas de RP obtenus", icon: BadgeCheck },
    { value: "382-456", label: "fourchette de scores CRS", icon: CalendarDays },
    { value: "2", label: "pays (Cameroun & Côte d'Ivoire)", icon: Globe2 },
  ],
  en: [
    { value: "14", label: "candidates accompanied", icon: UsersRound },
    { value: "100%", label: "PR visas obtained", icon: BadgeCheck },
    { value: "382-456", label: "CRS score range", icon: CalendarDays },
    { value: "2", label: "countries (Cameroon & Ivory Coast)", icon: Globe2 },
  ],
};

export default function About() {
  const { lang } = useLang();
  const c = page[lang] || page.fr;

  return (
    <div className="bg-alabaster" data-testid="about-page">
      <PageHero
        eyebrow={c.eyebrow}
        title={c.title}
        subtitle={c.subtitle}
        image={CANADA_MAPLE_IMAGE}
        imageAlt={lang === "fr" ? "Feuilles d’érable canadiennes" : "Canadian maple leaves"}
        compact
      />

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="aspect-[3/4] rounded-[2rem] overflow-hidden relative shadow-2xl border border-white"
          >
            <img src={FOUNDER_IMG} alt="Josias Ndam" className="w-full h-full object-cover" data-testid="about-portrait" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/65 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 bg-white/92 backdrop-blur-sm rounded-2xl p-5">
              <p className="font-serif text-2xl text-navy leading-none">Josias Ndam</p>
              <p className="text-xs tracking-[0.16em] uppercase text-redcanada mt-2">{c.role}</p>
            </div>
          </motion.div>
        </div>
        <div className="lg:col-span-6 lg:col-start-7 space-y-7 text-navy/78 leading-relaxed text-lg">
          <p>{c.intro1}</p>
          <p>{c.intro2}</p>
          <p>{c.intro3}</p>
          <div className="pt-7 border-t border-navy/10">
            <p className="font-serif text-3xl italic text-navy leading-snug">“{c.quote}”</p>
          </div>
        </div>
      </section>

      <section className="soft-sky-section py-24 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <p className="section-kicker">{c.experienceKicker}</p>
              <h2 className="section-title">{c.experienceTitle}</h2>
              <p className="section-subtitle max-w-3xl">{c.experienceText}</p>
            </div>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 xl:grid-cols-4 gap-5">
            {(stats[lang] || stats.fr).map(({ value, label, icon: Icon }, index) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="stat-card"
              >
                <div className="w-11 h-11 rounded-2xl bg-navy text-gold grid place-items-center"><Icon size={20} /></div>
                <p className="font-serif text-6xl text-navy mt-7 leading-none">{value}</p>
                <p className="mt-3 text-sm text-navy/60">{label}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-6 rounded-[2rem] bg-white border border-navy/8 p-8 sm:p-10 grid md:grid-cols-2 gap-x-12 gap-y-5">
            {c.snapshot.map((item) => (
              <div key={item} className="flex items-start gap-3 text-navy/72">
                <BadgeCheck size={19} className="text-redcanada shrink-0 mt-0.5" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-navy text-alabaster py-24 lg:py-28 relative overflow-hidden">
        <div className="absolute -top-44 -left-32 h-[520px] w-[520px] rounded-full bg-redcanada/15 blur-[130px]" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <p className="text-[10px] tracking-[0.3em] uppercase text-gold mb-4">— {lang === "fr" ? "Valeurs" : "Values"}</p>
          <h2 className="font-serif text-4xl lg:text-6xl tracking-tight leading-tight mb-16 max-w-2xl">{c.valuesTitle}</h2>
          <div className="grid md:grid-cols-2 gap-x-16 gap-y-2">
            {c.values.map((value, index) => (
              <motion.div
                key={value.k}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="border-t border-alabaster/15 py-8 grid grid-cols-12 gap-4"
                data-testid={`value-${index}`}
              >
                <span className="col-span-1 font-serif text-2xl text-redcanada">0{index + 1}</span>
                <div className="col-span-11">
                  <p className="font-serif text-3xl mb-2">{value.k}</p>
                  <p className="text-alabaster/70">{value.v}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
        <div className="rounded-[2rem] border border-redcanada/15 bg-white p-8 sm:p-10 lg:p-12 flex flex-col lg:flex-row gap-7 lg:items-center">
          <div className="w-14 h-14 rounded-2xl bg-redcanada/10 text-redcanada grid place-items-center shrink-0"><ShieldCheck size={25} /></div>
          <div>
            <h2 className="font-serif text-3xl text-navy">{c.noticeTitle}</h2>
            <p className="mt-3 text-sm sm:text-base leading-relaxed text-navy/65 max-w-5xl">{c.noticeText}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
